import React, { createContext, useContext, useEffect, useState } from 'react';
import { themeConfig } from '../theme/themeConfig';

// Create Theme Context
const ThemeContext = createContext();

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  // Get initial theme from localStorage or default to 'dark'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('portfolio-theme');
    return savedTheme || 'dark';
  });

  // Update theme in localStorage and document whenever it changes
  useEffect(() => {
    localStorage.setItem('portfolio-theme', theme);

    // Update HTML element's class for Tailwind dark mode
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Toggle between light and dark themes
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  // Set specific theme
  const setThemeMode = (mode) => {
    if (mode === 'light' || mode === 'dark') {
      setTheme(mode);
    }
  };

  // Get current theme config
  const currentTheme = themeConfig[theme];

  const value = {
    theme, // 'light' or 'dark'
    toggleTheme,
    setThemeMode,
    colors: currentTheme.colors,
    fonts: currentTheme.fonts,
    animations: themeConfig.animations,
    spacing: themeConfig.spacing,
    radius: themeConfig.radius,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
